import type {FC} from 'react';
import clsx from 'clsx';
import Link from 'next/link';

import {Badge} from '@/modules/finkn/components/badge';
import {Skeleton} from '@/modules/finkn/components/skeleton';
import {ArrowRightIcon} from '@/modules/foundation/components/icons/arrow-right-icon';
import {ImageBlock} from '@/modules/foundation/components/image/ImageBlock';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {formatDate} from '@/modules/foundation/shared/format';

import type {ArticleDetails} from '../../shared/types';

type Props = {
	article: ArticleDetails;
	headingLevel: 2 | 3;
};

export const ArticleCard: FC<Props> = ({article, headingLevel}) => {
	const {title, createdAt, href, imageWeb, description, badges} = article;

	const formattedDate = formatDate(createdAt);

	return (
		<Link href={href} className={clsx('block', '@container', 'group')}>
			<div
				className={clsx(
					'@2xl:grid',
					'gap-x-8',
					'gap-y-2',
					'@2xl:grid-cols-3',
				)}
			>
				{imageWeb && (
					<ImageBlock
						height={imageWeb.image.height}
						src={imageWeb.image.src}
						width={imageWeb.image.width}
						alt={imageWeb.altText ?? ''}
						className={clsx('rounded-xl', 'col-span-1')}
					/>
				)}
				<div className={clsx('col-span-2')}>
					<div
						className={clsx(
							'flex',
							'gap-x-4',
							'gap-y-1',
							'justify-between',
							'items-center',
							'@2xl:justify-normal',
							'flex-wrap',
							'py-2',
						)}
					>
						<time
							dateTime={createdAt}
							className={clsx('text-primary-5', 'text-xs')}
						>
							{formattedDate}
						</time>
						{badges?.length ? (
							<ul className={clsx('flex', 'gap-2', 'flex-wrap')}>
								{badges.map((badge) => (
									<li key={badge}>
										<Badge variant="outline">{badge}</Badge>
									</li>
								))}
							</ul>
						) : null}
					</div>

					<Heading
						level={headingLevel}
						spacing
						size="md"
						className={clsx(
							'flex',
							'items-center',
							'gap-4',
							'justify-between',
							'group-hover:text-primary-15',
							'transition-colors',
						)}
					>
						{title}
						<ArrowRightIcon
							aria-hidden
							className={clsx(
								'text-base',
								'text-grey-25',
								'shrink-0',
								// '@2xl:hidden',
								'mr-1',
								'group-hover:mr-0',
								'transition-all',
								'group-hover:text-primary-15',
							)}
						/>
					</Heading>
					{description && (
						<p className={clsx('line-clamp-3', 'text-sm')}>
							{description}
						</p>
					)}
				</div>
			</div>
		</Link>
	);
};

export const ArticleCardSkeleton = () => {
	return (
		<div className={clsx('@container')}>
			<div
				className={clsx(
					'@2xl:grid',
					'gap-x-8',
					'gap-y-2',
					'@2xl:grid-cols-3',
				)}
			>
				{/* Image */}
				<Skeleton
					className={clsx(
						'aspect-420/245',
						'rounded-lg',
						'col-span-1',
						'mb-1',
					)}
				/>
				<div className={clsx('col-span-2', 'space-y-1')}>
					{/* Date */}
					<Skeleton className={clsx('h-5', 'w-[10ch]')} />
					{/* Title */}
					<Skeleton className={clsx('h-6', 'w-3/5')} />
					{/* Description */}
					<Skeleton className={clsx('h-6', 'w-full')} />
					<Skeleton className={clsx('h-6', 'w-3/4')} />
				</div>
			</div>
		</div>
	);
};
